import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { useLanguage } from '../tools/useLanguage';

const styles = () => ({
  root: {
    padding: [0, 0, 80, 0]
  }
});

const creations = [
  {
    title: 'Digital Advertising',
    items: [
      'Facebook & social media ad campaigns',
      'Google Display advertising',
      'Programmatic ad creative',
      'A/B tested ad variations'
    ]
  },
  {
    title: 'Web & Landing Pages',
    items: [
      'High-converting landing page design',
      'Product showcase pages',
      'Campaign-specific microsites',
      'Responsive, mobile-optimized layouts'
    ]
  },
  {
    title: 'Brand & Campaign Design',
    items: [
      'Complete brand identity systems',
      'Campaign concepts and creative direction',
      'Marketing collateral and sales materials',
      'Brand guidelines and asset libraries'
    ]
  },
  {
    title: 'Out-of-Home',
    items: [
      'Billboard design',
      'Transit advertising',
      'Signage and environmental graphics',
      'Large-format print design'
    ]
  }
];

const processSteps = [
  { number: '01', title: 'Concept Development', text: 'Strategic creative concepts aligned with your goals' },
  { number: '02', title: 'Design & Production', text: 'Professional execution across all media formats' },
  { number: '03', title: 'Media Coordination', text: 'Placement and buying strategy support' },
  { number: '04', title: 'Performance Tracking', text: 'Analytics and optimization for maximum impact' },
  { number: '05', title: 'Multi-Channel Execution', text: 'Consistent campaigns across all touchpoints' }
];

const defaultCreative = {
  metaTitle: 'Creative - Texas Fortress AI',
  metaDescription: 'Full-service creative for brands that demand impact—ads, landing pages, brand systems, and campaigns that convert.',
  heroTitle: 'Ideas That Capture Attention',
  heroSubtitle: 'Full-service creative agency for brands that demand impact',
  introTitle: 'Creative That Converts',
  introText: `From concept to campaign, we craft visual experiences that stop the scroll, command attention, and drive results.
We're not just designers—we're strategic partners who understand that great creative serves your business goals.`,
  creationsTitle: 'What We Create',
  strategyTitle: 'Strategy Meets Design',
  strategyText: `Every pixel serves a purpose. We combine creative firepower with data-driven strategy to deliver campaigns that
don't just look good—they perform. Whether you need a complete brand overhaul or a single killer ad, we bring
the same level of craft and strategic thinking.`,
  processTitle: 'End-to-End Campaign Management',
  ctaText: 'Let\'s create something memorable — open chat to start now.',
  creations,
  processSteps
};

const CreativePage = ({ classes }) => {
  const { t } = useLanguage() || {};
  const content = t ? t('creative') : {};
  const metaTitle = content.metaTitle || defaultCreative.metaTitle;
  const metaDescription = content.metaDescription || defaultCreative.metaDescription;
  const heroTitle = content.heroTitle || defaultCreative.heroTitle;
  const heroSubtitle = content.heroSubtitle || defaultCreative.heroSubtitle;
  const introTitle = content.introTitle || defaultCreative.introTitle;
  const introText = content.introText || defaultCreative.introText;
  const creationsTitle = content.creationsTitle || defaultCreative.creationsTitle;
  const strategyTitle = content.strategyTitle || defaultCreative.strategyTitle;
  const strategyText = content.strategyText || defaultCreative.strategyText;
  const processTitle = content.processTitle || defaultCreative.processTitle;
  const ctaText = content.ctaText || defaultCreative.ctaText;
  const creationsList = content.creations || defaultCreative.creations;
  const processList = content.processSteps || defaultCreative.processSteps;
  const requestFitCallUrl = 'https://cal.com/your-handle/fit-call';

  const handleRequestFitCall = event => {
    const bridge = typeof window !== 'undefined' ? window.tfaiDeskBridge : null;
    if (bridge && typeof bridge.requestFitCall === 'function') {
      event.preventDefault();
      bridge.requestFitCall({ fallback: requestFitCallUrl });
    }
  };

  const handleFocusChat = event => {
    event.preventDefault();
    const bridge = typeof window !== 'undefined' ? window.tfaiDeskBridge : null;
    if (bridge && typeof bridge.focusInput === 'function') {
      bridge.focusInput();
      if (bridge.focusLoop) bridge.focusLoop();
    } else {
      if (typeof window !== 'undefined') {
        window.__TFAI_PENDING_FOCUS = true;
        const pollFocus = () => {
          const b = window.tfaiDeskBridge;
          if (b && typeof b.focusInput === 'function') {
            b.focusInput();
            if (b.focusLoop) b.focusLoop();
            window.__TFAI_PENDING_FOCUS = false;
            return true;
          }
          return false;
        };
        if (!pollFocus()) {
          const interval = setInterval(() => {
            if (pollFocus()) clearInterval(interval);
          }, 250);
          setTimeout(() => clearInterval(interval), 5000);
        }
      }
      handleRequestFitCall(event);
    }
  };

  return (
    <Main className={classes.root} flushLeft>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name='description' content={metaDescription} />
        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous' />
        <link href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap' rel='stylesheet' />
        <script src='https://cdn.tailwindcss.com' />
        <style>{`
          .creative-page {
            position: relative;
            min-height: 100vh;
            overflow-x: hidden;
            color: #ffffff;
            background: linear-gradient(180deg, #0a1628 0%, #1a2f4f 30%, #2d4a7c 60%, #4a6fa5 100%);
            font-family: 'Inter', 'Arial', sans-serif;
          }
          .creative-page::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background:
              radial-gradient(ellipse at 30% 20%, rgba(64, 165, 255, 0.12) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 80%, rgba(255, 215, 0, 0.12) 0%, transparent 50%),
              radial-gradient(ellipse at 50% 50%, rgba(12, 117, 192, 0.08) 0%, transparent 60%);
            z-index: -1;
            pointer-events: none;
            animation: creativeCosmicPulse 15s ease-in-out infinite;
          }
          @keyframes creativeCosmicPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 0.9; }
          }
          .creative-shell {
            position: relative;
            z-index: 1;
          }
          .creative-hero {
            min-height: 24vh;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 24px 12px 12px;
            position: relative;
            background: radial-gradient(ellipse at center, rgba(12, 117, 192, 0.18) 0%, transparent 70%);
          }
          .creative-hero h1 {
            font-size: 64px;
            font-weight: 800;
            letter-spacing: 4px;
            margin-bottom: 20px;
            text-transform: uppercase;
            background: linear-gradient(135deg, #ffffff 0%, #FFD700 60%, #19a4e7 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: creativeTitleGlow 3s ease-in-out infinite;
          }
          @keyframes creativeTitleGlow {
            0%, 100% { filter: brightness(1); }
            50% { filter: brightness(1.3); }
          }
          .creative-hero h2 {
            font-size: 24px;
            font-weight: 500;
            letter-spacing: 2px;
            color: #b8c5d6;
            max-width: 800px;
          }
          .creative-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 24px 12px 48px;
          }
          .creative-section {
            margin-bottom: 56px;
            opacity: 0;
            transform: translateY(30px);
            animation: creativeFadeInUp 0.8s ease forwards;
            animation-delay: var(--delay, 0s);
          }
          @keyframes creativeFadeInUp {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .creative-section-title {
            font-size: 42px;
            font-weight: 800;
            margin-bottom: 30px;
            text-align: center;
            letter-spacing: 3px;
            color: #FFD700;
            text-transform: uppercase;
          }
          .creative-section-intro {
            font-size: 20px;
            line-height: 1.8;
            color: #d4dce6;
            text-align: center;
            max-width: 900px;
            margin: 0 auto 28px;
          }
          .creative-services-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 30px;
            margin-top: 50px;
          }
          .creative-service-card {
            background: rgba(26, 47, 79, 0.6);
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 15px;
            padding: 32px 28px;
            transition: all 0.4s ease;
            position: relative;
            overflow: hidden;
          }
          .creative-service-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, rgba(255, 215, 0, 0.12) 0%, rgba(19, 164, 231, 0.08) 100%);
            opacity: 0;
            transition: opacity 0.4s ease;
          }
          .creative-service-card:hover {
            transform: translateY(-10px);
            border-color: rgba(255, 215, 0, 0.6);
            box-shadow: 0 20px 40px rgba(19, 164, 231, 0.25);
          }
          .creative-service-card:hover::before {
            opacity: 1;
          }
          .creative-service-card h3 {
            font-size: 22px;
            color: #FFD700;
            margin-bottom: 15px;
            letter-spacing: 1px;
            position: relative;
            z-index: 1;
          }
          .creative-service-card ul {
            list-style: none;
            margin-top: 12px;
            position: relative;
            z-index: 1;
            padding: 0;
          }
          .creative-service-card ul li {
            font-size: 15px;
            color: #d4dce6;
            padding: 8px 0 8px 25px;
            position: relative;
          }
          .creative-service-card ul li::before {
            content: '▸';
            position: absolute;
            left: 0;
            color: #FFD700;
            font-size: 18px;
          }
          .creative-process {
            background: rgba(26, 47, 79, 0.4);
            border-radius: 20px;
            padding: 60px 32px;
            border: 1px solid rgba(255, 215, 0, 0.3);
          }
          .creative-process-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 22px;
            margin-top: 34px;
          }
          .creative-process-item {
            text-align: center;
            padding: 28px 18px;
            background: rgba(74, 111, 165, 0.3);
            border-radius: 15px;
            transition: all 0.3s ease;
            border: 1px solid rgba(255, 215, 0, 0.2);
          }
          .creative-process-item:hover {
            background: rgba(74, 111, 165, 0.5);
            transform: scale(1.05);
            border-color: rgba(255, 215, 0, 0.5);
          }
          .creative-process-number {
            font-size: 46px;
            font-weight: 800;
            color: #FFD700;
            margin-bottom: 12px;
          }
          .creative-process-item h4 {
            font-size: 18px;
            color: #FFD700;
            margin-bottom: 8px;
            letter-spacing: 1px;
          }
          .creative-process-item p {
            font-size: 14px;
            color: #b8c5d6;
            line-height: 1.6;
          }
          .creative-cta {
            text-align: center;
            padding: 80px 20px;
            background: radial-gradient(ellipse at center, rgba(19, 164, 231, 0.12) 0%, transparent 70%);
          }
          .creative-cta-button {
            display: inline-block;
            padding: 18px 48px;
            font-size: 18px;
            font-weight: 800;
            letter-spacing: 2px;
            color: #0a1628;
            background: linear-gradient(135deg, #FFD700 0%, #f2a900 100%);
            border: none;
            border-radius: 50px;
            text-decoration: none;
            text-transform: uppercase;
            transition: all 0.4s ease;
            box-shadow: 0 10px 30px rgba(255, 215, 0, 0.35);
            cursor: pointer;
          }
          .creative-cta-button:hover {
            transform: translateY(-5px) scale(1.05);
            box-shadow: 0 15px 40px rgba(19, 164, 231, 0.35);
          }
          .creative-footer {
            background: rgba(10, 22, 40, 0.95);
            padding: 40px 20px;
            text-align: center;
            border-top: 1px solid rgba(255, 215, 0, 0.3);
          }
          .creative-footer p {
            color: #b8c5d6;
            font-size: 14px;
            letter-spacing: 1px;
          }
          @media (max-width: 900px) {
            .creative-hero {
              min-height: 60vh;
              height: auto;
              padding-top: 64px;
            }
          }
          @media (max-width: 768px) {
            .creative-hero h1 {
              font-size: 42px;
            }
            .creative-hero h2 {
              font-size: 18px;
            }
            .creative-section-title {
              font-size: 32px;
            }
          }
        `}</style>
      </Helmet>

      <div className='creative-page'>
        <div className='creative-shell'>
          <section className='creative-hero'>
            <h1>{heroTitle}</h1>
            <h2>{heroSubtitle}</h2>
          </section>

          <div className='creative-container'>
            <section className='creative-section' style={{ '--delay': '0.2s' }}>
              <h2 className='creative-section-title'>{introTitle}</h2>
              <p className='creative-section-intro'>
                {introText}
              </p>
            </section>

            <section className='creative-section' style={{ '--delay': '0.4s' }}>
              <h2 className='creative-section-title'>{creationsTitle}</h2>
              <div className='creative-services-grid'>
                {creationsList.map(block => (
                  <div key={block.title} className='creative-service-card'>
                    <h3>{block.title}</h3>
                    <ul>
                      {block.items.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section className='creative-section' style={{ '--delay': '0.6s' }}>
              <h2 className='creative-section-title'>{strategyTitle}</h2>
              <p className='creative-section-intro'>
                {strategyText}
              </p>
            </section>

            <section className='creative-section' style={{ '--delay': '0.8s' }}>
              <div className='creative-process'>
                <h2 className='creative-section-title'>{processTitle}</h2>
                <div className='creative-process-grid'>
                  {processList.map(step => (
                    <div key={step.number} className='creative-process-item'>
                      <div className='creative-process-number'>{step.number}</div>
                      <h4>{step.title}</h4>
                      <p>{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <section className='creative-cta' id='contact'>
            <button
              type='button'
              className='creative-cta-button'
              onClick={handleFocusChat}
            >
              {ctaText}
            </button>
          </section>
        </div>
      </div>
    </Main>
  );
};

CreativePage.propTypes = {
  classes: PropTypes.object
};

export default withStyles(styles)(CreativePage);
